<script setup lang="ts">
/**
 * SettingsModal · 设置中心
 * ------------------------------------------------------------
 * 对齐 v1.6 原型交互：租户/组织/外观/语言等低频设置全部收拢于此，
 * 顶栏只留「账号与设置」入口。左侧分区导航 + 右侧内容。
 *
 * 分区与状态（M2）：
 * - 实装：外观与语言 / 个人信息（展示）/ 助手偏好 / 角色与权限（演示 fixture）/
 *   账号与安全（本机演示）/ 通知偏好 / 默认与偏好 / 数据与隐私 / 快捷键 / 关于
 * - M2+ 规划：个人信息编辑（HR 主数据）、RBAC 对接权限中心、偏好同步账号云端
 */
import { nameDisplay, setNameMode, type NameMode } from '@/shared/display-name'
import { ref, computed, watch, nextTick } from 'vue'
import { useScopeStore } from '@/domain/scope/store'
import {
  PORTAL_THEMES,
  portalTenant, portalBrandName, portalThemeId, portalTenantSite,
  portalAccount, portalDept, portalAvatar,
  setTheme, setBrandName, setTenantSite, resetAppearance,
  setAvatar, resetAvatar
} from './portal/portal'
import { useTheme } from './theme/useTheme'
import { useI18n, LOCALES } from '@/design/i18n'
import { useToast } from '@/design/ui/UToast'
import FoldTab from '@/shared/FoldTab.vue'
import { useColFold } from '@/shared/useColFold'
import { loadMemory, removeMemory, clearMemory, type MemoryItem } from '@/shared/memory'
import { assistantProfile, setAssistantName, setAssistantAvatar, ASSISTANT_AVATAR_PRESETS } from '@/shared/assistant'
import { NOTIFY_LEVELS, NOTIFY_CHANNELS, NOTIFY_MATRIX_DEFAULTS } from '@/data/mock/notify'
import { customScenes, loadCustomScenes, saveCustomScene, removeCustomScene, type SceneRow, type SceneDraft } from '@/shared/custom-scenes'
import { assistantApi } from '@/data/api/assistant'
import { inviteCodesApi, type CodeContext, type MyInviteCode } from '@/data/api/invite-codes'

type SecId = 'appearance' | 'profile' | 'assistant' | 'rbac' | 'security' | 'plancode' | 'notify' | 'defaults' | 'shortcuts' | 'privacy' | 'about'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'update:open', v: boolean): void }>()

const scope = useScopeStore()
const { mode, setMode } = useTheme()
const { lang, setLang } = useI18n()
/* 左侧分区导航可折叠（UI 强制规范：FoldTab 手柄） */
const { folded: navFolded, toggle: toggleNav } = useColFold('settings')

/* 时区（持久化到 localStorage，日程等页面读取展示） */
const TIMEZONES = [
  'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Taipei', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Seoul',
  'Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Moscow',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'America/Sao_Paulo',
  'Australia/Sydney', 'Pacific/Auckland', 'UTC'
]
const TZ_KEY = 'ema.tz.v1'
const tz = ref(localStorage.getItem(TZ_KEY) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai')
watch(tz, v => { try { localStorage.setItem(TZ_KEY, v) } catch { /* 忽略 */ } })

const sec = ref<SecId>('profile')
const rootEl = ref<HTMLElement | null>(null)

/* 个人信息 · 聊天称呼显示偏好（存本机；昵称本人在右上角「账号与设置」弹层维护，随账号生效） */
function onNameMode(e: Event) { setNameMode((e.target as HTMLSelectElement).value as NameMode) }

const SECTIONS: { id: SecId; name: string; icon: string; stub?: boolean }[] = [
  { id: 'profile', name: '个人信息', icon: '◉' },
  { id: 'assistant', name: '助手偏好', icon: '✦' },
  { id: 'notify', name: '通知偏好', icon: '◎' },
  { id: 'rbac', name: '角色与权限', icon: '▣' },
  { id: 'security', name: '账号与安全', icon: '◈' },
  { id: 'plancode', name: '套餐与邀请', icon: '❖' },
  { id: 'appearance', name: '外观与语言', icon: '◐' },
  { id: 'defaults', name: '默认与偏好', icon: '★' },
  { id: 'privacy', name: '数据与隐私', icon: '◆' },
  { id: 'shortcuts', name: '快捷键', icon: '⌘' },
  { id: 'about', name: '关于', icon: 'ℹ' }
]
const curName = computed(() => SECTIONS.find(s => s.id === sec.value)?.name ?? '')

/* 外观与语言 */
const brandDraft = ref('')
/* 企业官网地址：品牌区点击跳转的目标（留空即回落租户默认官网） */
const siteDraft = ref('')
const brandStatus = ref('')
watch(() => props.open, v => {
  if (!v) return
  brandDraft.value = portalBrandName.value
  siteDraft.value = portalTenantSite.value
  brandStatus.value = ''
  sec.value = 'profile'
  nextTick(() => rootEl.value?.focus())
})
function saveBrand() {
  const name = brandDraft.value.trim()
  if (!name) { brandStatus.value = '请填写品牌展示名称。'; return }
  const site = siteDraft.value.trim()
  if (site && !setTenantSite(site)) { brandStatus.value = '官网地址需以 http:// 或 https:// 开头。'; return }
  if (!site) setTenantSite('')
  setBrandName(name)
  brandStatus.value = '已保存到当前浏览器。'
}
function doReset() {
  resetAppearance()
  brandDraft.value = portalBrandName.value
  siteDraft.value = portalTenantSite.value
  brandStatus.value = '已恢复此租户默认外观。'
}


/* 角色与权限数据（对接 RBAC 后从接口取，当前为演示 fixture） */
const USER_ROLES = [
  { name: '流程与信息化专员', source: '系统', domain: '全局', desc: '架构浏览 · 流程设计 · 异常处理' },
  { name: '异常台账管理员', source: '系统', domain: '制造', desc: '异常登记 · 处置闭环 · 上报' },
  { name: '供应链协调员', source: '业务', domain: '供应链', desc: '跨组织调拨 · 索赔' },
  { name: '合同审核员', source: '业务', domain: '合同', desc: '合同条款审核 · 风险标注' },
  { name: '数据只读者', source: '系统', domain: '全局', desc: '只读全部应用域，不可写' }
]

/* 功能权限 400+：按应用域分组 */
const FUNC_DOMAINS: { domain: string; perms: string[] }[] = [
  { domain: '供应链管理', perms: ['采购订单查看', '采购订单审批', '供应商档案管理', '库存查询', '库存调拨申请', '调拨审批', '入库确认', '出库确认', '盘点发起', '盘点结果录入', '退货处理', '索赔登记', '索赔审批', '物流跟踪', '仓储布局管理'] },
  { domain: '制造管理', perms: ['生产订单查看', '生产订单下达', '工单确认', '报工录入', '质检记录', '返工登记', '设备状态查看', '设备维保申请', '产能查询', '排产调整', 'BOM 查看', '工艺路线查看', '生产批次追溯'] },
  { domain: '财务管理', perms: ['凭证查看', '凭证录入', '凭证审核', '科目余额查询', '预算执行查询', '费用报销', '报销审批', '付款申请', '付款审批', '收款登记', '银行对账', '资产折旧查看', '资产盘点', '税务申报查看', '成本核算查看'] },
  { domain: '合同管理', perms: ['合同起草', '合同审核', '合同签订', '合同变更', '合同终止', '条款模板管理', '风险标注', '到期提醒', '履约跟踪', '档案查询'] },
  { domain: '项目管理', perms: ['项目立项', '项目计划', '任务分派', '进度更新', '里程碑管理', '资源分配', '成本归集', '验收确认', '结项审批'] },
  { domain: '贸易管理', perms: ['订单接收', '报价管理', '客户档案', '信用额度查看', '发货确认', '退货审批', '价格审批', '佣金计算'] },
  { domain: '人力资源', perms: ['花名册查看', '入职登记', '离职审批', '调岗审批', '薪酬查询', '考勤查看', '绩效录入', '培训管理', '招聘需求', '面试记录'] },
  { domain: '数据管理', perms: ['主数据查看', '数据字典管理', '数据质量监控', '数据标准管理', '数据资产目录', '血缘查看', '清洗规则配置'] },
  { domain: '设备管理', perms: ['设备档案', '点检录入', '维修工单', '备件管理', '计量器具管理', '故障登记'] },
  { domain: '能源与碳', perms: ['能耗录入', '碳排放查看', '碳配额管理', '减排计划', '报告导出'] },
  { domain: '企业职能', perms: ['公文起草', '公文审批', '会议管理', '议题管理', '决议跟踪', '制度管理'] },
  { domain: '数字平台', perms: ['用户管理', '角色管理', '权限配置', '组织架构管理', '日志审计', '系统配置', '接口管理', '流程引擎配置'] }
]

/* 数据权限 100+：按维度分组 */
const DATA_PERMS: { group: string; items: string[] }[] = [
  { group: '组织范围', items: ['本组织全部', '集团总部只读', '华东制造只读', '华南销售只读', '西南能源只读'] },
  { group: '应用域', items: FUNC_DOMAINS.map(d => `${d.domain}（读）`, ) } as any,
  { group: '敏感字段', items: ['薪酬字段（本人）', '薪酬字段（部门汇总）', '合同金额（读）', '成本数据（读）', '客户信用额度（读）', '供应商报价（读）', '员工绩效（本部门）', '银行账号（脱敏）'] },
  { group: '时间维度', items: ['当月', '当季', '当年', '历史 3 年', '历史 5 年', '全量'] }
]

/* 范围权限 */
const SCOPE_PERMS = [
  '供应链 · 制造 · 人力资源', '合同 · 财务（只读）', '项目管理（执行）',
  '贸易（只读）', '数据管理（管理）', '设备（读）', '能源与碳（读）',
  '企业职能（起草+审批）', '数字平台（只读）', '全组织跨域只读'
]

/* RBAC 状态 */
const rbacSearch = ref('')
const expandedDomains = ref<Set<string>>(new Set())
const expandedDataGroups = ref<Set<string>>(new Set())
function toggleSet(s: Set<string>, key: string) { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n }
function isExpanded(s: Set<string>, key: string) { return s.has(key) }
const filteredDomains = computed(() => {
  const kw = rbacSearch.value.trim().toLowerCase()
  if (!kw) return FUNC_DOMAINS
  return FUNC_DOMAINS.map(d => ({
    ...d, perms: d.perms.filter(p => p.toLowerCase().includes(kw))
  })).filter(d => d.perms.length > 0)
})
const filteredDataPerms = computed(() => {
  const kw = rbacSearch.value.trim().toLowerCase()
  if (!kw) return DATA_PERMS
  return DATA_PERMS.map(g => ({
    ...g, items: g.items.filter(i => i.toLowerCase().includes(kw))
  })).filter(g => g.items.length > 0)
})
const totalFuncPerms = computed(() => FUNC_DOMAINS.reduce((s, d) => s + d.perms.length, 0))
const totalDataPerms = computed(() => DATA_PERMS.reduce((s, g) => s + g.items.length, 0))

/* 助手偏好 · 长期工作偏好（AI 建议与操作参考；与「我的」签名共用 ema.profile.v1，merge 写入防覆盖） */
const PREF_KEY = 'ema.profile.v1'
function loadProfileRaw(): { signature?: string; prefs?: string[] } {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}') || {} } catch { return {} }
}
const PREF_PRESETS = ['结论先行', '用数据说话', '表格优于长文', '重要事项主动预警', '供应链专业口径', '决策需列依据', '正式商务语气', '先给方案再确认']
const prefSel = ref<string[]>(loadProfileRaw().prefs ?? ['结论先行', '用数据说话', '供应链专业口径'])
const prefDraft = ref('')
const prefCustom = computed(() => prefSel.value.filter(p => !PREF_PRESETS.includes(p)))
function persistPrefs() {
  localStorage.setItem(PREF_KEY, JSON.stringify({ ...loadProfileRaw(), prefs: prefSel.value }))
}
function togglePref(p: string) {
  const i = prefSel.value.indexOf(p)
  if (i >= 0) prefSel.value.splice(i, 1)
  else prefSel.value.push(p)
  persistPrefs()
}
function addPref() {
  const p = prefDraft.value.trim()
  if (!p || prefSel.value.includes(p)) { prefDraft.value = ''; return }
  prefSel.value.push(p)
  prefDraft.value = ''
  persistPrefs()
}

/* 场景定制：自然语言描述 → AI 生成草稿（不落库）→ 本人编辑确认后保存到账号下 */
const sceneDesc = ref('')
const sceneBusy = ref(false)
const sceneSaving = ref(false)
const sceneError = ref('')
const sceneDraft = ref<(SceneDraft & { id?: string }) | null>(null)
const sceneList = computed(() => customScenes.list)
async function genScene() {
  const text = sceneDesc.value.trim()
  if (!text || sceneBusy.value) return
  sceneBusy.value = true
  sceneError.value = ''
  try {
    sceneDraft.value = await assistantApi.generateScene(text)
    sceneDesc.value = ''
  } catch (e) { sceneError.value = e instanceof Error ? e.message : '场景没有生成，请重试' }
  finally { sceneBusy.value = false }
}
async function saveSceneDraft() {
  const d = sceneDraft.value
  if (!d || sceneSaving.value || !d.name.trim()) return
  sceneSaving.value = true
  sceneError.value = ''
  try {
    await saveCustomScene({ ...d, name: d.name.trim(), questions: d.questions.map(q => q.trim()).filter(Boolean) })
    sceneDraft.value = null
    toast.show('场景已保存，聊天页场景入口已更新')
  } catch (e) { sceneError.value = e instanceof Error ? e.message : '场景没有保存，请重试' }
  finally { sceneSaving.value = false }
}
function editScene(s: SceneRow) {
  sceneError.value = ''
  sceneDraft.value = { id: s.id, name: s.name, greeting: s.greeting, instruction: s.instruction, questions: [...s.questions] }
}
async function delScene(id: string) {
  sceneError.value = ''
  try { await removeCustomScene(id); toast.show('已删除这个场景') }
  catch (e) { sceneError.value = e instanceof Error ? e.message : '没有删除，请重试' }
}

/* 本人头像：上传图片 → 画布压缩为 256px 内小图 → 按账号持久化本机（portal 身份域）；
   顶栏、聊天、讨论、个人信息等全站展示读 portalAvatar 同步跟随 */
const avatarFileEl = ref<HTMLInputElement | null>(null)
const avatarErr = ref('')
function pickAvatar() { avatarErr.value = ''; avatarFileEl.value?.click() }
function doResetAvatar() { resetAvatar(); avatarErr.value = '' }
function onAvatarFile(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (!file.type.startsWith('image/')) { avatarErr.value = '请选择图片文件'; return }
  const reader = new FileReader()
  reader.onload = () => {
    const img = new Image()
    img.onload = () => {
      const MAX = 256
      const scale = Math.min(1, MAX / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const cv = document.createElement('canvas')
      cv.width = w; cv.height = h
      const ctx = cv.getContext('2d')
      if (!ctx) { avatarErr.value = '当前浏览器不支持图片处理，请换浏览器重试'; return }
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(img, 0, 0, w, h)
      setAvatar(cv.toDataURL('image/jpeg', 0.85))
      avatarErr.value = ''
    }
    img.onerror = () => { avatarErr.value = '图片读取失败，请换一张试试' }
    img.src = String(reader.result)
  }
  reader.onerror = () => { avatarErr.value = '文件读取失败，请重试' }
  reader.readAsDataURL(file)
}

/* 账号与安全（大企业登录模式：SSO 主认证 + 手机/微信/邮箱 + 2FA + 设备与会话管理。
   2FA/会话/微信绑定状态持久化 ema.security.v1；换绑与设备下线为演示 toast，M2 对接账号安全中心） */
const toast = useToast()
const SEC_KEY = 'ema.security.v1'
function loadSec(): { mfa?: boolean; mfaMethods?: string[]; session?: string; wechat?: boolean } {
  try { return JSON.parse(localStorage.getItem(SEC_KEY) || '{}') || {} } catch { return {} }
}
const secSaved = loadSec()
const mfaOn = ref(secSaved.mfa ?? true)
const mfaMethods = ref<string[]>(secSaved.mfaMethods ?? ['sms', 'wechat'])
const sessionTtl = ref(secSaved.session ?? '8h')
const wechatBound = ref(secSaved.wechat ?? false)
watch([mfaOn, mfaMethods, sessionTtl, wechatBound], () => {
  localStorage.setItem(SEC_KEY, JSON.stringify({ mfa: mfaOn.value, mfaMethods: mfaMethods.value, session: sessionTtl.value, wechat: wechatBound.value }))
}, { deep: true })
const MFA_OPTIONS = [
  { id: 'sms', name: '短信验证码', desc: '登录时向 138****6688 发送 6 位验证码' },
  { id: 'totp', name: 'TOTP 动态口令', desc: 'Authenticator 类 App · 30 秒刷新，离线可用' },
  { id: 'wechat', name: '微信扫码确认', desc: '登录时在企业微信 / 微信中一键确认' }
]
function toggleMfa(id: string) {
  const i = mfaMethods.value.indexOf(id)
  if (i >= 0) mfaMethods.value.splice(i, 1)
  else mfaMethods.value.push(id)
}
function bindWechat() { wechatBound.value = true; toast.show(`微信「${portalAccount.value.name}的微信」绑定成功（演示）`) }
function unbindWechat() { wechatBound.value = false; toast.show('已解绑微信；快捷登录将不可用（演示）') }
const SESSIONS = [
  { id: '8h', name: '8 小时（单个工作日）' },
  { id: '24h', name: '24 小时' },
  { id: '7d', name: '7 天' },
  { id: '30d', name: '30 天' }
]
const devices = ref([
  { cur: true, name: 'Windows · Edge 浏览器', loc: '上海', time: '当前会话' },
  { cur: false, name: 'iPhone 15 Pro · 企管助手 App', loc: '上海', time: '3 天前' },
  { cur: false, name: '会议室平板 · 华东制造基地', loc: '常州', time: '10-02' }
])
function kickDevice(i: number) {
  const d = devices.value[i]
  devices.value.splice(i, 1)
  toast.show(`已下线「${d.name}」，其会话立即失效（演示）`)
}
const LOGIN_LOGS = [
  { t: '今天 09:12', desc: '上海 · Windows / Edge · SSO 登录', ok: '成功', bad: false },
  { t: '10-09 20:35', desc: '上海 · iPhone · 企管助手 App · 微信确认', ok: '成功', bad: false },
  { t: '10-08 14:02', desc: '非常用地 · 密码错误 2 次', ok: '已拦截', bad: true }
]

/* 通知偏好（持久化 localStorage，通知中心按此过滤） */
const NOTIFY_KEY = 'ema.notify.v1'
function loadNotify(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(NOTIFY_KEY) || '{}') || {} } catch { return {} }
}
const notifyPrefs = ref([
  { key: 'todo', name: '待办与任务指派', on: loadNotify().todo !== false },
  { key: 'approval', name: '审批进展', on: loadNotify().approval !== false },
  { key: 'announce', name: '公司公告', on: loadNotify().announce !== false },
  { key: 'group', name: '讨论群消息', on: loadNotify().group === true }
])
watch(notifyPrefs, v => {
  const map: Record<string, boolean> = {}
  v.forEach(p => { map[p.key] = p.on })
  try { localStorage.setItem(NOTIFY_KEY, JSON.stringify(map)) } catch { /* 忽略 */ }
}, { deep: true })

/* 通知方式（来源 × 重要程度 → 渠道多选下拉。级别与渠道选项统一由 data/mock/notify.ts 维护，
   新增级别/渠道时本页零改动；浏览器推送走真实 Notification 授权，微信需先绑定；
   矩阵持久化 ema.notify.matrix.v1，实际通道下发 M2 接入后按此执行） */
const NM_KEY = 'ema.notify.matrix.v1'
function loadNmMatrix(): Record<string, Record<string, string[]>> {
  let saved: Record<string, Record<string, string[]>> = {}
  try { saved = JSON.parse(localStorage.getItem(NM_KEY) || '{}') || {} } catch { saved = {} }
  const base: Record<string, Record<string, string[]>> = {}
  for (const src of Object.keys(NOTIFY_MATRIX_DEFAULTS)) {
    base[src] = {}
    for (const lv of NOTIFY_LEVELS) {
      const s = saved[src]?.[lv.key]
      base[src][lv.key] = Array.isArray(s)
        ? s.filter(c => NOTIFY_CHANNELS.some(x => x.key === c))
        : [...(NOTIFY_MATRIX_DEFAULTS[src]?.[lv.key] ?? [])]
    }
  }
  return base
}
const notifyMatrix = ref(loadNmMatrix())
watch(notifyMatrix, v => {
  try { localStorage.setItem(NM_KEY, JSON.stringify(v)) } catch { /* 忽略 */ }
}, { deep: true })
function nmOn(src: string, lv: string, ch: string): boolean {
  return notifyMatrix.value[src]?.[lv]?.includes(ch) ?? false
}
function nmHas(src: string, lv: string): boolean {
  return (notifyMatrix.value[src]?.[lv]?.length ?? 0) > 0
}
function nmSummary(src: string, lv: string): string {
  const sel = notifyMatrix.value[src]?.[lv] ?? []
  if (!sel.length) return '不通知'
  return NOTIFY_CHANNELS.filter(c => sel.includes(c.key)).map(c => c.name).join('、')
}
/* 渠道多选浮层：同一时间只开一个；透明遮罩层点击关闭（不用全局监听，避免热更新残留旧监听器导致开了又秒关） */
const nmPicker = ref<{ src: string; lv: string; up: boolean } | null>(null)
function nmClosePicker() { nmPicker.value = null }
function nmPickerToggle(src: string, lv: string, ev?: MouseEvent) {
  if (nmPicker.value?.src === src && nmPicker.value?.lv === lv) { nmClosePicker(); return }
  /* 下方空间不足时向上展开，避免浮层被弹窗底边裁掉 */
  let up = false
  const btn = ev?.currentTarget as HTMLElement | null
  const sc = btn?.closest('.set-scroll')
  if (btn && sc) {
    const b = btn.getBoundingClientRect()
    const c = sc.getBoundingClientRect()
    up = (c.bottom - b.bottom) < 300 && (b.top - c.top) > (c.bottom - b.bottom)
  }
  nmPicker.value = { src, lv, up }
}
watch(sec, () => nmClosePicker())
async function nmToggle(srcKey: string, lv: string, ch: string) {
  const arr = notifyMatrix.value[srcKey]?.[lv]
  if (!arr) return
  const turningOn = !arr.includes(ch)
  if (turningOn && ch === 'wechat' && !wechatBound.value) {
    toast.show('请先在「账号与安全」绑定微信')
    return
  }
  if (turningOn && ch === 'push') {
    if (!('Notification' in window)) { toast.show('当前浏览器不支持桌面推送'); return }
    try {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') { toast.show('浏览器未授权通知权限，可在浏览器站点设置中开启'); return }
    } catch { toast.show('通知授权失败，请重试'); return }
  }
  const i = arr.indexOf(ch)
  if (i >= 0) arr.splice(i, 1)
  else arr.push(ch)
}
function nmSetAll(srcKey: string, lv: string, all: boolean) {
  const row = notifyMatrix.value[srcKey]?.[lv]
  if (!row) return
  if (!all) { row.splice(0, row.length); return }
  const skip: string[] = []
  const next = NOTIFY_CHANNELS.map(c => c.key).filter(k => {
    if (k === 'wechat' && !wechatBound.value) { skip.push('微信'); return false }
    if (k === 'push' && (!('Notification' in window) || Notification.permission !== 'granted')) { skip.push('浏览器推送'); return false }
    return true
  })
  row.splice(0, row.length, ...next)
  if (skip.length) toast.show(`${skip.join('、')}未就绪，已跳过（可单独开启）`)
}

/* 数据与隐私：长期记忆在账号下（chat.memory），删除和清除走后端 */
const mems = ref<MemoryItem[]>([])
const memError = ref('')
async function reloadMem() {
  try { mems.value = await loadMemory(); memError.value = '' }
  catch (e) { memError.value = e instanceof Error ? e.message : '记忆没有读出来' }
}
async function delMem(id: string) {
  try { mems.value = await removeMemory(id); memError.value = ''; toast.show('已删除这条记忆') }
  catch (e) { memError.value = e instanceof Error ? e.message : '没有删除，请重试' }
}
async function wipeMem() {
  try { mems.value = await clearMemory(); memError.value = ''; toast.show('已清除全部长期记忆') }
  catch (e) { const message = e instanceof Error ? e.message : '没有清除，请重试'; memError.value = message; toast.show(message) }
}
const dataScope = computed(() => ({
  perm: scope.current?.perm ?? '—',
  domains: scope.current?.domains?.length ?? 0,
  lockGrps: scope.current?.lockGrps?.length ?? 0
}))
const emaKeys = () => Object.keys(localStorage).filter(k => k.startsWith('ema.'))
const localCount = ref(emaKeys().length)
watch(() => props.open, v => { if (v) { localCount.value = emaKeys().length; void reloadMem(); void loadCustomScenes() } })
function exportLocal() {
  const dump: Record<string, unknown> = {}
  emaKeys().forEach(k => {
    const raw = localStorage.getItem(k)
    try { dump[k] = raw ? JSON.parse(raw) : null } catch { dump[k] = raw }
  })
  const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), keys: dump }, null, 2)], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'ema-本机数据导出.json'
  a.click()
  URL.revokeObjectURL(a.href)
  toast.show(`已导出 ${emaKeys().length} 项本机数据`)
}
function wipeLocal() {
  const n = emaKeys().length
  if (!window.confirm(`将清除本机全部 ${n} 项 EMA 数据（偏好、收藏、通知已读等），并刷新页面。账号下的长期记忆不会被删掉。确定继续？`)) return
  emaKeys().forEach(k => localStorage.removeItem(k))
  location.reload()
}

/* 默认与偏好（默认进入页：登录/回首页时落位） */
/* 移动端主页面：显隐 + 排序（ema.mobile.home.v1；移动端壳层按此渲染底栏，默认主页 = 排序第一位的已启用页面） */
const MOBILE_PAGES = [
  { key: 'chat', name: '聊天' },
  { key: 'tasks', name: '任务' },
  { key: 'calendar', name: '日程' },
  { key: 'cloud', name: '空间' },
  { key: 'apps', name: '应用' },
  { key: 'arch', name: '架构' },
  { key: 'cloudpc', name: '云电脑' },
  { key: 'profile', name: '我的' }
]
const MOBILE_KEY = 'ema.mobile.home.v1'
function loadMobile(): { key: string; name: string; on: boolean }[] {
  let saved: any[] | null = null
  try {
    const raw = JSON.parse(localStorage.getItem(MOBILE_KEY) || 'null')
    /* 完整性校验：数组、页数一致、key 全部可识别，否则视为脏数据回退默认（全显、标准顺序） */
    if (Array.isArray(raw) && raw.length === MOBILE_PAGES.length &&
        MOBILE_PAGES.every(p => raw.some((x: any) => x && x.key === p.key))) saved = raw
  } catch { /* 忽略 */ }
  return MOBILE_PAGES.map(p => {
    const s = saved ? saved.find((x: any) => x.key === p.key) : null
    return { ...p, on: s ? s.on !== false : true }
  })
}
const mobilePages = ref(loadMobile())
watch(mobilePages, v => {
  try { localStorage.setItem(MOBILE_KEY, JSON.stringify(v.map(({ key, on }) => ({ key, on })))) } catch { /* 忽略 */ }
}, { deep: true })
const mobileOnCount = computed(() => mobilePages.value.filter(p => p.on).length)
function moveMobile(i: number, dir: -1 | 1) {
  const j = i + dir
  if (j < 0 || j >= mobilePages.value.length) return
  const arr = [...mobilePages.value];
  [arr[i], arr[j]] = [arr[j], arr[i]]
  mobilePages.value = arr
}
function toggleMobile(p: { key: string; on: boolean }) {
  if (p.on && mobileOnCount.value <= 1) { toast.show('至少保留一个移动端主页面'); return }
  p.on = !p.on
}

const SHORTCUTS = [
  { keys: '/', desc: '唤起全局搜索' },
  { keys: 'Ctrl+K', desc: '新建会话' },
  { keys: 'Esc', desc: '返回上级 / 关闭弹层' },
  { keys: 'Alt+←', desc: '后退' },
  { keys: 'Alt+H', desc: '回到首页' }
]

function close() { emit('update:open', false) }
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { e.stopPropagation(); close() } }

/* 套餐与邀请（实装 /api/codes：当前套餐 / 兑换码 / 我的邀请码） */
const pcCtx = ref<CodeContext | null>(null)
const pcLoading = ref(false)
const pcError = ref('')
const redeemCodeInput = ref('')
const redeemBusy = ref(false)
const redeemMsg = ref('')
const redeemErr = ref('')
const policyId = ref('')
const genBusy = ref(false)
const genErr = ref('')
const copyOkId = ref('')
const INVITE_STATUS: Record<string, string> = { active: '未兑换', redeemed: '已兑换', revoked: '已作废', expired: '已过期' }

function fmtDay(d: unknown): string {
  const t = new Date(String(d))
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
}
function tierLabel(v: string): string {
  return pcCtx.value?.serviceTiers.find(t => t.value === v)?.label || v
}
async function loadPlanCode() {
  pcLoading.value = true; pcError.value = ''
  try {
    pcCtx.value = await inviteCodesApi.context()
    policyId.value = pcCtx.value.policies[0]?.id || ''
  } catch (e: any) {
    pcError.value = e?.message || '加载失败，请稍后重试'
  } finally { pcLoading.value = false }
}
watch(sec, v => {
  if (v === 'plancode' && props.open) { redeemMsg.value = ''; redeemErr.value = ''; void loadPlanCode() }
})
async function doRedeem() {
  const code = redeemCodeInput.value.trim()
  if (!code || redeemBusy.value) return
  redeemBusy.value = true; redeemErr.value = ''; redeemMsg.value = ''
  try {
    const r = await inviteCodesApi.redeem(code)
    redeemMsg.value = `已激活「${r.planName}」${r.trialDays} 天试用，${fmtDay(r.expireAt)} 到期后自动回落为原套餐`
    redeemCodeInput.value = ''
    await loadPlanCode()
  } catch (e: any) {
    redeemErr.value = e?.message || '兑换失败，请稍后重试'
  } finally { redeemBusy.value = false }
}
async function doGenerate() {
  if (genBusy.value) return
  genBusy.value = true; genErr.value = ''
  try {
    await inviteCodesApi.generateInvite(policyId.value || undefined)
    await loadPlanCode()
  } catch (e: any) {
    genErr.value = e?.message || '生成失败，请稍后重试'
  } finally { genBusy.value = false }
}
async function doRevokeInvite(id: string) {
  try { await inviteCodesApi.revokeInvite(id); await loadPlanCode() } catch (e: any) { genErr.value = e?.message || '作废失败，请稍后重试' }
}
function inviteText(c: MyInviteCode): string {
  return `我正在使用 EMA 企业智能管理平台，送你「${c.planName}」${c.trialDays} 天试用。登录后在 设置中心 → 套餐与邀请 → 兑换码 输入邀请码 ${c.code} 即可激活（${fmtDay(c.expireAt)} 前有效）。`
}
async function copyInvite(c: MyInviteCode) {
  try {
    await navigator.clipboard.writeText(inviteText(c))
    copyOkId.value = c.id
    setTimeout(() => { if (copyOkId.value === c.id) copyOkId.value = '' }, 2000)
  } catch { toast.show('复制失败，请手动选择复制') }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="set-veil" @click.self="close">
      <section class="set-panel" :class="{ 'f-nav': navFolded }" role="dialog" aria-label="设置中心" tabindex="-1" ref="rootEl"
               @keydown="onKey">
        <!-- 左侧分区导航 -->
        <nav class="set-nav" :class="{ 'f-off': navFolded }">
          <h2>设置</h2>
          <button v-for="s in SECTIONS" :key="s.id" class="set-nav-item" :class="{ on: s.id === sec }"
                  @click="sec = s.id">
            <span class="set-nav-ic" :class="{ txt: s.icon.length <= 1 }">{{ s.icon }}</span>
            <span class="set-nav-nm">{{ s.name }}</span>
            <span v-if="s.stub" class="set-stub-tag">规划中</span>
          </button>
        </nav>

        <!-- 左栏折叠手柄（UI 强制规范：17×46 贴边竖条，栏底角内侧） -->
        <FoldTab class="set-tab" :on="navFolded" label="设置导航" @toggle="toggleNav" />

        <!-- 右侧内容 -->
        <div class="set-body">
          <header class="set-head">
            <h3>{{ curName }}</h3><select class="set-mobile-section" aria-label="设置分区" v-model="sec"><option v-for="s in SECTIONS" :key="s.id" :value="s.id">{{s.name}}</option></select>
            <button class="set-close" aria-label="关闭设置" @click="close">×</button>
          </header>

          <div class="set-scroll">
            <!-- 外观与语言 -->
            <template v-if="sec === 'appearance'">
              <div class="set-sec">
                <label class="set-lb">界面明暗</label>
                <div class="set-seg">
                  <button :class="{ on: mode === 'light' }" @click="setMode('light')">浅色</button>
                  <button :class="{ on: mode === 'dark' }" @click="setMode('dark')">深色</button>
                </div>
              </div>
              <div class="set-sec">
                <label class="set-lb">语言与地区</label>
                <select :value="lang" class="set-input" aria-label="语言与地区"
                        @change="setLang(($event.target as HTMLSelectElement).value)">
                  <option v-for="l in LOCALES" :key="l.id" :value="l.id">
                    {{ l.native }}（{{ l.region }}）{{ l.ready ? '' : ' · 规划中' }}
                  </option>
                </select>
                <p class="set-note">未实装翻译的语言将暂时回退到相近语言界面；翻译包随 M2 国际化逐步补齐。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">时区</label>
                <select v-model="tz" class="set-input">
                  <option v-for="z in TIMEZONES" :key="z" :value="z">{{ z }}</option>
                </select>
                <p class="set-note">默认取系统时区；跨区时间换算随 M2 实装生效。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">主题色</label>
                <div class="set-palette">
                  <button v-for="(t, id) in PORTAL_THEMES" :key="id" class="set-theme"
                          :class="{ on: id === portalThemeId }" @click="setTheme(String(id))">
                    <span class="set-swatch" :style="{ background: `linear-gradient(110deg, ${t.header}, ${t.end})` }" />
                    {{ t.name }}{{ id === portalThemeId ? ' ✓' : '' }}
                  </button>
                </div>
              </div>
              <div class="set-sec">
                <label class="set-lb">品牌展示名称（{{ portalTenant.name }}）</label>
                <input class="set-input" v-model="brandDraft" type="text" maxlength="30">
                <p class="set-note">顶栏左侧品牌区展示的企业名称。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">企业官网地址（点击品牌区跳转）</label>
                <input class="set-input" v-model="siteDraft" type="url" maxlength="120"
                       placeholder="https://www.example.com">
                <p class="set-note">{{ brandStatus || `留空即使用默认官网 ${portalTenant.site}；品牌区点击在新标签打开。` }}</p>
                <div class="set-row-btns">
                  <button class="set-primary" @click="saveBrand">保存</button>
                  <button class="set-link" @click="doReset">恢复此租户默认外观</button>
                </div>
              </div>
            </template>

            <!-- 个人信息（身份字段跟随当前登录账号） -->
            <template v-else-if="sec === 'profile'">
              <div class="set-userhero">
                <button class="set-avatar-btn" type="button" title="点击更换头像" @click="pickAvatar">
                  <img v-if="portalAvatar" class="set-avatar" :src="portalAvatar" :alt="portalAccount.name + '的头像'">
                  <span v-else class="set-avatar set-avatar-txt">{{ portalAccount.initial }}</span>
                </button>
                <span>
                  <b>{{ portalAccount.name }}</b>
                  <span class="set-tag">{{ portalAccount.mode }}</span>
                  <small>{{ portalAccount.id }} · {{ scope.current?.name ?? '—' }} · {{ portalDept }}</small>
                </span>
              </div>
              <div class="set-avatar-acts">
                <button class="set-link" type="button" @click="pickAvatar">更换头像</button>
                <button v-if="portalAvatar" class="set-link" type="button" @click="doResetAvatar">移除头像</button>
                <small>在顶栏、聊天、讨论与个人信息中同步展示</small>
              </div>
              <p v-if="avatarErr" class="set-note" role="alert">{{ avatarErr }}</p>
              <input ref="avatarFileEl" type="file" accept="image/*" hidden @change="onAvatarFile">
              <div class="set-sec">
                <label class="set-lb">基本档案</label>
                <div class="set-fgrid">
                  <div class="set-field"><small>姓名</small><b>{{ portalAccount.name }}</b></div>
                  <div class="set-field"><small>职位</small><b>流程与信息化专员</b></div>
                  <div class="set-field"><small>当前部门（挂职可切换）</small><b>{{ portalDept }}</b></div>
                  <div class="set-field"><small>工号</small><b>EMP-1024</b></div>
                  <div class="set-field"><small>企业邮箱</small><b>{{ portalAccount.id }}@aierp.demo</b></div>
                  <div class="set-field"><small>手机</small><b>138****6688</b></div>
                </div>
                <p class="set-note">档案字段对接 HR 主数据与账号中心后开放编辑（规划中）；多账号切换在右上角「账号与设置」菜单。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">聊天称呼</label>
                <div class="set-fgrid">
                  <div class="set-field set-field-wide"><small>我看到别人的称呼</small>
                    <select class="set-select" :value="nameDisplay.mode" aria-label="聊天称呼显示" @change="onNameMode">
                      <option value="nick">昵称优先（对方未设昵称时显示姓名）</option>
                      <option value="name">姓名</option>
                    </select>
                  </div>
                </div>
                <p class="set-note">称呼显示偏好只影响本机聊天、讨论与群文件中人员名字的展示；我的昵称在右上角头像「账号与设置」弹层修改。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">当前执行口径</label>
                <div class="set-fgrid">
                  <div class="set-field"><small>执行口径</small><b>{{ scope.current?.perm ?? '—' }}</b></div>
                  <div class="set-field"><small>可用应用域</small><b>{{ scope.current?.domains?.length ?? 0 }} 个</b></div>
                  <div class="set-field"><small>锁定应用组</small><b>{{ scope.current?.lockGrps?.length ?? 0 }} 个</b></div>
                </div>
              </div>
            </template>

            <!-- 助手偏好 · 长期工作偏好（实装，自动保存） -->
            <template v-else-if="sec === 'assistant'">
              <!-- 助手形象：昵称 + 头像（实装，自动保存；聊天页助理消息头据此展示） -->
              <div class="set-sec">
                <label class="set-lb">助手形象</label>
                <div class="as-id">
                  <span class="as-id-av">{{ assistantProfile.av || '✦' }}</span>
                  <span class="as-id-info">
                    <b>{{ assistantProfile.name || '企管助手' }}</b>
                    <small>助手在对话中以这里的昵称与头像出现</small>
                  </span>
                </div>
                <div class="as-avs">
                  <button v-for="a in ASSISTANT_AVATAR_PRESETS" :key="a" class="as-av"
                          :class="{ on: assistantProfile.av === a }" :title="'使用头像 ' + a"
                          @click="setAssistantAvatar(a)">{{ a }}</button>
                  <input class="as-av-custom" :value="assistantProfile.av" maxlength="2" placeholder="自定"
                         title="自定义头像（emoji 或单字）"
                         @input="setAssistantAvatar(($event.target as HTMLInputElement).value)">
                </div>
                <input class="set-input as-name" :value="assistantProfile.name" maxlength="12" placeholder="企管助手"
                       aria-label="助手昵称"
                       @input="setAssistantName(($event.target as HTMLInputElement).value)">
                <p class="set-note">留空即使用默认助手名「企管助手」与按业务推导的头像；自动保存在当前浏览器。</p>
              </div>

              <!-- 场景定制：自然语言描述 → AI 生成草稿 → 确认保存到账号下（实装） -->
              <div class="set-sec">
                <label class="set-lb">场景定制</label>
                <p class="set-note as-desc">
                  用一两句话描述您想要的工作场景，AI 会生成场景草稿（名称、开场白、场景口径与建议问题）。
                  保存后出现在聊天页的场景入口；在该场景中提问，助手会按场景口径回答。
                </p>
                <div class="sc-add">
                  <textarea v-model="sceneDesc" rows="2" maxlength="2000"
                            placeholder="例如：我负责华东区采购对账，每周要盯逾期付款和到货偏差"></textarea>
                  <button :disabled="sceneBusy || !sceneDesc.trim()" @click="genScene">
                    {{ sceneBusy ? '正在生成…' : '生成场景' }}
                  </button>
                </div>
                <p v-if="sceneError" class="set-note" role="alert">{{ sceneError }}</p>

                <!-- 草稿卡片：可编辑后保存；未保存前不落库 -->
                <div v-if="sceneDraft" class="sc-card">
                  <div class="sc-row"><small>场景名</small>
                    <input v-model="sceneDraft.name" maxlength="30" placeholder="场景名">
                  </div>
                  <div class="sc-row"><small>开场白</small>
                    <input v-model="sceneDraft.greeting" maxlength="120" placeholder="未开始对话时的副标题">
                  </div>
                  <div class="sc-row"><small>场景口径（助手在这个场景里的默认行为）</small>
                    <textarea v-model="sceneDraft.instruction" rows="3" maxlength="500"></textarea>
                  </div>
                  <div class="sc-row"><small>建议问题（{{ sceneDraft.questions.length }}/6）</small>
                    <div v-for="i in sceneDraft.questions.length" :key="i" class="sc-q">
                      <input v-model="sceneDraft.questions[i - 1]" maxlength="60" placeholder="用户在这个场景里常问的问题">
                      <button class="sc-q-x" title="删除这条" @click="sceneDraft.questions.splice(i - 1, 1)">×</button>
                    </div>
                    <button v-if="sceneDraft.questions.length < 6" class="set-link" @click="sceneDraft.questions.push('')">+ 加一条</button>
                  </div>
                  <div class="set-row-btns">
                    <button class="set-primary" :disabled="sceneSaving || !sceneDraft.name.trim()" @click="saveSceneDraft">
                      {{ sceneSaving ? '正在保存…' : (sceneDraft.id ? '保存修改' : '保存场景') }}
                    </button>
                    <button class="set-link" @click="sceneDraft = null">放弃</button>
                  </div>
                </div>

                <!-- 已保存场景 -->
                <div v-for="s in sceneList" :key="s.id" class="sc-item">
                  <span class="sc-item-b">
                    <b>{{ s.name }}</b>
                    <small>{{ s.greeting || '—' }} · {{ s.questions.length }} 个建议问题</small>
                  </span>
                  <button class="set-link" @click="editScene(s)">编辑</button>
                  <button class="set-link" @click="delScene(s.id)">删除</button>
                </div>
                <p v-if="!sceneList.length && !sceneDraft" class="set-note">还没有自定义场景。生成并保存后，会出现在聊天页的场景入口。</p>
                <p class="set-note">场景保存在当前账号下；场景口径只影响您的对话回答方式，不作为业务事实。</p>
              </div>

              <div class="set-sec">
                <label class="set-lb">让企管助手更懂您</label>
                <p class="set-note as-desc">
                  选择或填写符合您个人习惯的条目，企管助手将按您的专业度与偏好来提供建议、汇报与操作——
                  例如按「结论先行」组织回答、用供应链口径解读数据。
                </p>
                <div class="as-chips">
                  <button v-for="p in PREF_PRESETS" :key="p" class="as-chip" :class="{ on: prefSel.includes(p) }"
                          :title="prefSel.includes(p) ? '点击移除' : '点击启用'" @click="togglePref(p)">
                    <i>{{ prefSel.includes(p) ? '✓' : '+' }}</i>{{ p }}
                  </button>
                  <span v-for="p in prefCustom" :key="p" class="as-chip custom on">
                    <i>✓</i>{{ p }}<b title="移除" @click="togglePref(p)">×</b>
                  </span>
                </div>
                <div class="as-add">
                  <input v-model="prefDraft" maxlength="30" placeholder="添加自定义偏好，如：每次回复附风险提示"
                         @keydown.enter="addPref">
                  <button @click="addPref">添加</button>
                </div>
                <div class="as-live">
                  <span class="as-ai">AI</span>
                  <p v-if="prefSel.length">
                    已启用 <b>{{ prefSel.length }}</b> 条偏好：{{ prefSel.join(' · ') }}。
                    企管助手将在建议、汇报与操作中始终参考这些习惯。
                  </p>
                  <p v-else>暂未启用偏好，启用后企管助手将按您的个人习惯提供建议。</p>
                </div>
                <p class="set-note">偏好自动保存在当前浏览器；M2 同步 AI 服务后全局生效。</p>
              </div>
            </template>

            <!-- 角色与权限 -->
            <template v-else-if="sec === 'rbac'">
              <!-- 汇总 -->
              <div class="rbac-summary">
                <div class="rbac-stat"><b>{{ USER_ROLES.length }}</b><small>系统角色</small></div>
                <div class="rbac-stat"><b>{{ totalFuncPerms }}</b><small>功能权限</small></div>
                <div class="rbac-stat"><b>{{ totalDataPerms }}</b><small>数据权限</small></div>
                <div class="rbac-stat"><b>{{ SCOPE_PERMS.length }}</b><small>范围权限</small></div>
              </div>

              <!-- 角色 -->
              <div class="set-sec">
                <label class="set-lb">系统角色（{{ USER_ROLES.length }} 个）</label>
                <div class="rbac-role-grid">
                  <div v-for="r in USER_ROLES" :key="r.name" class="rbac-role-card">
                    <div class="rbac-role-head">
                      <b>{{ r.name }}</b>
                      <span class="rbac-role-src" :class="{ sys: r.source === '系统' }">{{ r.source }}</span>
                    </div>
                    <small>{{ r.domain }}</small>
                    <p>{{ r.desc }}</p>
                  </div>
                </div>
              </div>

              <!-- 搜索 -->
              <div class="set-sec">
                <label class="set-lb">功能权限（{{ totalFuncPerms }} 项）· 数据权限（{{ totalDataPerms }} 项）</label>
                <input class="set-input rbac-search" v-model="rbacSearch" type="text" placeholder="搜索功能/数据权限关键词…" maxlength="20">
              </div>

              <!-- 功能权限：按应用域分组折叠 -->
              <div class="set-sec">
                <label class="set-lb">功能权限 · 按应用域</label>
                <div v-for="d in filteredDomains" :key="d.domain" class="rbac-group">
                  <button class="rbac-group-head" @click="expandedDomains = toggleSet(expandedDomains, d.domain)">
                    <span class="rbac-chev">{{ isExpanded(expandedDomains, d.domain) ? '▾' : '▸' }}</span>
                    <b>{{ d.domain }}</b>
                    <i>{{ d.perms.length }} 项</i>
                  </button>
                  <div v-if="isExpanded(expandedDomains, d.domain)" class="rbac-group-body">
                    <span v-for="p in d.perms" :key="p" class="set-tag-item">{{ p }}</span>
                  </div>
                </div>
                <p v-if="filteredDomains.length === 0" class="set-note">无匹配的功能权限。</p>
              </div>

              <!-- 数据权限：按维度分组折叠 -->
              <div class="set-sec">
                <label class="set-lb">数据权限 · 按维度</label>
                <div v-for="g in filteredDataPerms" :key="g.group" class="rbac-group">
                  <button class="rbac-group-head" @click="expandedDataGroups = toggleSet(expandedDataGroups, g.group)">
                    <span class="rbac-chev">{{ isExpanded(expandedDataGroups, g.group) ? '▾' : '▸' }}</span>
                    <b>{{ g.group }}</b>
                    <i>{{ g.items.length }} 项</i>
                  </button>
                  <div v-if="isExpanded(expandedDataGroups, g.group)" class="rbac-group-body">
                    <span v-for="item in g.items" :key="item" class="set-tag-item">{{ item }}</span>
                  </div>
                </div>
                <p v-if="filteredDataPerms.length === 0" class="set-note">无匹配的数据权限。</p>
              </div>

              <!-- 范围权限 -->
              <div class="set-sec">
                <label class="set-lb">临时授权（{{ SCOPE_PERMS.length }} 项）</label>
                <div class="set-tags">
                  <span v-for="s in SCOPE_PERMS" :key="s" class="set-tag-item">{{ s }}</span>
                </div>
              </div>

              <p class="set-note">角色与权限对接 RBAC 权限中心后实时同步（规划中）；当前为演示 fixture，功能权限按应用域分组折叠、支持搜索过滤。</p>
            </template>

            <!-- 账号与安全（实装：大企业登录模式演示） -->
            <template v-else-if="sec === 'security'">
              <div class="set-sec">
                <label class="set-lb">登录方式</label>
                <div class="sec-m">
                  <span class="sec-m-ic on">SSO</span>
                  <span class="sec-m-b">
                    <b>企业统一身份（SSO）<i class="sec-m-tag">主认证</i></b>
                    <small>集团 IdP 托管 · 密码策略与锁定规则由集团统一管理</small>
                  </span>
                  <button class="sec-m-act" @click="toast.show('跳转集团身份管理平台（M2 对接 IdP 后开放）')">管理</button>
                </div>
                <div class="sec-m">
                  <span class="sec-m-ic on">手机</span>
                  <span class="sec-m-b">
                    <b>138****6688</b>
                    <small>可用于登录验证与安全提醒</small>
                  </span>
                  <button class="sec-m-act" @click="toast.show('换绑手机号需先验证原号码短信（M2 开放）')">换绑</button>
                </div>
                <div class="sec-m">
                  <span class="sec-m-ic" :class="{ on: wechatBound }">微信</span>
                  <span class="sec-m-b">
                    <b>{{ wechatBound ? portalAccount.name + '的微信' : '未绑定' }}</b>
                    <small>{{ wechatBound ? '可扫码快捷登录与接收审批提醒' : '绑定后支持扫码快捷登录' }}</small>
                  </span>
                  <button v-if="!wechatBound" class="sec-m-act" @click="bindWechat">绑定</button>
                  <button v-else class="sec-m-act" @click="unbindWechat">解绑</button>
                </div>
                <div class="sec-m">
                  <span class="sec-m-ic on">@</span>
                  <span class="sec-m-b">
                    <b>{{ portalAccount.id }}@aierp.demo</b>
                    <small>企业邮箱 · 接收安全通知与账号告警</small>
                  </span>
                  <button class="sec-m-act" @click="toast.show('企业邮箱由 HR 主数据同步，个人不可修改（M2）')">管理</button>
                </div>
              </div>

              <div class="set-sec">
                <label class="set-lb">双因子认证（2FA）</label>
                <div class="set-toggle-row">
                  <span>开启双因子认证</span>
                  <button class="set-toggle" :class="{ on: mfaOn }" role="switch" :aria-checked="mfaOn"
                          @click="mfaOn = !mfaOn"><i /></button>
                </div>
                <template v-if="mfaOn">
                  <div v-for="m in MFA_OPTIONS" :key="m.id" class="set-toggle-row">
                    <span>{{ m.name }}<small class="sec-sub">{{ m.desc }}</small></span>
                    <button class="set-toggle" :class="{ on: mfaMethods.includes(m.id) }" role="switch"
                            :aria-checked="mfaMethods.includes(m.id)" @click="toggleMfa(m.id)"><i /></button>
                  </div>
                </template>
                <p class="set-note">{{ mfaOn ? '登录时需通过任一已启用方式完成二次验证；建议至少启用两种。' : '当前仅 SSO 单因子登录，仅限企业内网环境，建议开启。' }}</p>
              </div>

              <div class="set-sec">
                <label class="set-lb">会话有效期</label>
                <select v-model="sessionTtl" class="set-input">
                  <option v-for="s in SESSIONS" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
                <p class="set-note">超过有效期需重新通过 SSO 登录；薪酬、审批等敏感操作始终要求二次确认。</p>
              </div>

              <div class="set-sec">
                <label class="set-lb">登录设备</label>
                <div v-for="(d, i) in devices" :key="d.name" class="sec-m">
                  <span class="sec-m-ic" :class="{ on: d.cur }">{{ d.cur ? '本' : '端' }}</span>
                  <span class="sec-m-b">
                    <b>{{ d.name }}<i v-if="d.cur" class="sec-m-tag">本机</i></b>
                    <small>{{ d.loc }} · {{ d.time }}</small>
                  </span>
                  <button v-if="!d.cur" class="sec-m-act" @click="kickDevice(i)">下线</button>
                </div>
                <p class="set-note">发现可疑设备请立即下线并联系 IT 安全部。</p>
              </div>

              <div class="set-sec">
                <label class="set-lb">最近登录</label>
                <div v-for="l in LOGIN_LOGS" :key="l.t" class="sec-log">
                  <span class="sec-log-t">{{ l.t }}</span>
                  <span class="sec-log-b">{{ l.desc }}</span>
                  <span class="sec-log-ok" :class="{ bad: l.bad }">{{ l.ok }}</span>
                </div>
              </div>
              <p class="set-note">绑定状态、设备与会话为演示数据（2FA 与会话偏好保存在当前浏览器）；M2 对接企业 SSO / 账号安全中心后同步真实状态。</p>
            </template>

            <!-- 套餐与邀请（实装 /api/codes） -->
            <template v-else-if="sec === 'plancode'">
              <div v-if="pcLoading" class="set-sec"><p class="set-note">加载中…</p></div>
              <template v-else-if="pcCtx">
                <div class="set-sec">
                  <label class="set-lb">当前套餐</label>
                  <div class="sec-m">
                    <span class="sec-m-ic on">◆</span>
                    <span class="sec-m-b">
                      <b>{{ pcCtx.trial ? pcCtx.trial.planName : (pcCtx.subscription?.planName || '—') }}<i v-if="pcCtx.trial" class="sec-m-tag">试用中</i></b>
                      <small v-if="pcCtx.trial">{{ pcCtx.trial.planName }}试用中 · {{ fmtDay(pcCtx.trial.expireAt) }} 到期回落为{{ pcCtx.subscription?.planName || '原套餐' }}</small>
                      <small v-else-if="pcCtx.subscription">有效期至 {{ fmtDay(pcCtx.subscription.expireAt) }}</small>
                      <small v-else>当前企业暂无订阅信息</small>
                    </span>
                  </div>
                </div>

                <div class="set-sec">
                  <label class="set-lb">兑换码</label>
                  <div class="pc-row">
                    <input v-model="redeemCodeInput" class="set-input pc-grow" placeholder="输入邀请码或激活码" maxlength="32" @keyup.enter="doRedeem" />
                    <button class="sec-m-act" :disabled="redeemBusy || !redeemCodeInput.trim()" @click="doRedeem">{{ redeemBusy ? '兑换中…' : '兑换' }}</button>
                  </div>
                  <p v-if="redeemMsg" class="set-note pc-ok">{{ redeemMsg }}</p>
                  <p v-else-if="redeemErr" class="set-note pc-err">{{ redeemErr }}</p>
                  <p v-else class="set-note">兑换成功立即激活对应套餐限时试用，到期自动回落为原套餐；同一时间仅允许一个试用生效。</p>
                </div>

                <div class="set-sec">
                  <label class="set-lb">我的邀请码</label>
                  <template v-if="pcCtx.enabled">
                    <div class="pc-row">
                      <select v-if="pcCtx.policies.length > 1" v-model="policyId" class="set-input pc-grow">
                        <option v-for="p in pcCtx.policies" :key="p.id" :value="p.id">{{ p.targetPlanName }} · {{ p.trialDays }} 天 · {{ tierLabel(p.serviceTier) }}</option>
                      </select>
                      <button class="sec-m-act" :disabled="genBusy || !pcCtx.policies.length || pcCtx.used >= pcCtx.max" @click="doGenerate">{{ genBusy ? '生成中…' : '生成邀请码' }}</button>
                      <span class="set-note">还可生成 {{ Math.max(0, pcCtx.max - pcCtx.used) }} 个</span>
                    </div>
                    <p v-if="genErr" class="set-note pc-err">{{ genErr }}</p>
                    <p v-if="!pcCtx.policies.length" class="set-note">当前套餐暂无可用发码策略，请联系平台开通。</p>
                    <div v-for="c in pcCtx.codes" :key="c.id" class="sec-m">
                      <span class="sec-m-ic" :class="{ on: c.status === 'active' }">码</span>
                      <span class="sec-m-b">
                        <b>{{ c.code }}<i class="sec-m-tag">{{ c.planName }} · {{ c.trialDays }} 天</i></b>
                        <small>{{ INVITE_STATUS[c.status] || c.status }} · {{ fmtDay(c.expireAt) }} 前可兑换</small>
                      </span>
                      <button v-if="c.status === 'active'" class="sec-m-act" @click="copyInvite(c)">{{ copyOkId === c.id ? '已复制' : '复制' }}</button>
                      <button v-if="c.status === 'active'" class="sec-m-act" @click="doRevokeInvite(c.id)">作废</button>
                    </div>
                    <p v-if="!pcCtx.codes.length" class="set-note">还没有邀请码，生成后点「复制」把邀请文案发给伙伴即可。</p>
                  </template>
                  <p v-else class="set-note">邀请码功能已关闭，如有需要请联系平台。</p>
                </div>
              </template>
              <div v-else class="set-sec">
                <p class="set-note pc-err">{{ pcError || '加载失败' }}</p>
                <button class="sec-m-act" @click="loadPlanCode">重试</button>
              </div>
            </template>

            <!-- 通知偏好（持久化） -->
            <template v-else-if="sec === 'notify'">
              <div class="set-sec">
                <label class="set-lb">接收提醒</label>
                <div v-for="p in notifyPrefs" :key="p.key" class="set-toggle-row">
                  <span>{{ p.name }}</span>
                  <button class="set-toggle" :class="{ on: p.on }" role="switch" :aria-checked="p.on"
                          @click="p.on = !p.on"><i /></button>
                </div>
                <p class="set-note">关闭的分类不再出现在通知中心与角标；偏好保存在当前浏览器，M2 后同步到账号云端。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">通知方式（按来源与重要程度选择渠道）</label>
                <div v-for="src in notifyPrefs" :key="src.key" class="nm-block" :class="{ off: !src.on }">
                  <div class="nm-title">{{ src.name }}<small v-if="!src.on">（已在上方关闭接收，开启后可配置）</small></div>
                  <div v-for="lv in NOTIFY_LEVELS" :key="lv.key" class="nm-row">
                    <span class="nm-lv">{{ lv.name }}</span>
                    <button type="button" class="nm-sel" :disabled="!src.on"
                            :aria-expanded="nmPicker?.src === src.key && nmPicker?.lv === lv.key"
                            @click="nmPickerToggle(src.key, lv.key, $event)">
                      <span class="nm-sum" :class="{ empty: !nmHas(src.key, lv.key) }">{{ nmSummary(src.key, lv.key) }}</span>
                      <span class="nm-caret">▾</span>
                    </button>
                    <div v-if="nmPicker?.src === src.key && nmPicker?.lv === lv.key" class="nm-mask" @click="nmClosePicker" />
                    <div v-if="nmPicker?.src === src.key && nmPicker?.lv === lv.key" class="nm-pop" :class="{ up: nmPicker.up }" @click.stop>
                      <div v-for="ch in NOTIFY_CHANNELS" :key="ch.key" class="nm-opt" @click="nmToggle(src.key, lv.key, ch.key)">
                        <i class="nm-ck" :class="{ on: nmOn(src.key, lv.key, ch.key) }"><svg viewBox="0 0 12 12" width="9" height="9"><path d="M2 6.2 4.8 9 10 3.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg></i>{{ ch.name }}
                      </div>
                      <div class="nm-acts">
                        <button type="button" @click="nmSetAll(src.key, lv.key, true)">全选</button>
                        <button type="button" @click="nmSetAll(src.key, lv.key, false)">清空</button>
                      </div>
                    </div>
                  </div>
                </div>
                <p class="set-note">紧急＝需立即处理（如超时催办、全员必达），重要＝需及时关注，一般＝知会即可。浏览器推送开启时需授权浏览器通知；各通道实际下发在 M2 接入通知服务后按此执行，偏好保存在当前浏览器，M2 后同步账号云端。</p>
              </div>
            </template>

            <!-- 默认与偏好（实装） -->
            <template v-else-if="sec === 'defaults'">
              <div class="set-sec">
                <label class="set-lb">默认进入页</label>
                <div class="set-input">聊天（固定首页）</div>
                <p class="set-note">系统根入口、登录后及返回首页均进入聊天。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">移动端主页面</label>
                <p class="set-note mb-tip">
                  控制移动端主页面的显示与排序；系统默认入口统一为聊天。
                </p>
                <div v-for="(p, i) in mobilePages" :key="p.key" class="mb-row" :class="{ off: !p.on }">
                  <span class="mb-idx">{{ i + 1 }}</span>
                  <b class="mb-nm">{{ p.name }}</b>

                  <span class="mb-ops">
                    <button class="mb-btn" :disabled="i === 0" title="上移" @click="moveMobile(i, -1)">↑</button>
                    <button class="mb-btn" :disabled="i === mobilePages.length - 1" title="下移" @click="moveMobile(i, 1)">↓</button>
                  </span>
                  <button class="set-toggle" :class="{ on: p.on }" role="switch" :aria-checked="p.on"
                          :title="p.on ? '在移动端隐藏' : '在移动端显示'" @click="toggleMobile(p)"><i /></button>
                </div>
                <p class="set-note">
                  已启用 {{ mobileOnCount }} / {{ mobilePages.length }} 个 · 排序与显隐保存在当前浏览器，M2 同步账号云端后全设备生效。
                </p>
              </div>
              <div class="set-sec">
                <label class="set-lb">默认组织</label>
                <p class="set-note">规划中：M2 接账号服务后记录上次使用的组织作为下次登录默认。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">执行 / 审批口径与数据范围</label>
                <p class="set-note">规划中：随组织权限模型下发，个人不可自选；当前以组织档案口径为准。</p>
              </div>
            </template>

            <!-- 快捷键 -->
            <template v-else-if="sec === 'shortcuts'">
              <div class="set-sec">
                <label class="set-lb">键盘快捷键</label>
                <div v-for="s in SHORTCUTS" :key="s.keys" class="set-kbd-row">
                  <kbd>{{ s.keys }}</kbd><span>{{ s.desc }}</span>
                </div>
              </div>
            </template>

            <!-- 数据与隐私（实装） -->
            <template v-else-if="sec === 'privacy'">
              <div class="set-sec">
                <label class="set-lb">助理长期记忆（{{ mems.length }} 条）</label>
                <div v-for="m in mems" :key="m.id" class="pv-mem">
                  <span>{{ m.text }}</span>
                  <button class="set-link" @click="delMem(m.id)">删除</button>
                </div>
                <p v-if="memError" class="set-note">{{ memError }}</p>
                <p v-else-if="!mems.length" class="set-note">还没有长期记忆。在右侧栏写下要记住的偏好后，会出现在这里。</p>
                <div v-if="mems.length" class="set-row-btns"><button class="set-link" @click="wipeMem">清除全部记忆</button></div>
                <p class="set-note">记忆保存在当前账号下。浏览器里以前的演示句子不会写入账号。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">数据可见范围（随组织权限下发 · 只读）</label>
                <div class="set-fgrid">
                  <div class="set-field"><small>执行口径</small><b>{{ dataScope.perm }}</b></div>
                  <div class="set-field"><small>可用应用域</small><b>{{ dataScope.domains }} 个</b></div>
                  <div class="set-field"><small>锁定应用组</small><b>{{ dataScope.lockGrps }} 个</b></div>
                </div>
                <p class="set-note">数据范围由组织权限模型统一管控，个人不可自选。</p>
              </div>
              <div class="set-sec">
                <label class="set-lb">本机数据（{{ localCount }} 项）</label>
                <div class="set-row-btns">
                  <button class="set-primary" @click="exportLocal">导出本机数据（JSON）</button>
                  <button class="set-link" @click="wipeLocal">清除本机数据并重置</button>
                </div>
                <p class="set-note">包含偏好、收藏、通知已读等 ema.* 本机键。长期记忆在账号下，清除本机数据不会删掉已记下的记忆。</p>
              </div>
            </template>

            <!-- 关于 -->
            <template v-else>
              <div class="set-sec">
                <label class="set-lb">版本信息</label>
                <p class="set-about">
                  <b>EMA 企管助手</b><br>
                  AIERP V1.02 · 交互原型工程化 Web 版<br>
                  数据层：原型演示数据（架构库 / 组织画像 / 场景与会话）<br>
                  设计基线：交互原型 v1.6（2026-10-10）
                </p>
              </div>
            </template>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.set-mobile-section{display:none}@media(max-width:760px){.set-mobile-section{display:block;max-width:50%;padding:6px;color:var(--ink);background:var(--surface);border:1px solid var(--line)}}
.set-veil { position: fixed; inset: 0; z-index: 400; background: #0f152480; backdrop-filter: blur(2px); display: grid; place-items: center; }
.set-panel {
  position: relative;
  display: grid; grid-template-columns: var(--set1, 200px) minmax(0, 1fr); grid-template-rows: minmax(0, 1fr);
  width: min(880px, calc(100vw - 40px)); height: min(620px, calc(100dvh - 80px));
  background: var(--surface); border: 1px solid var(--line); border-radius: 16px;
  box-shadow: var(--sh-3); overflow: hidden; outline: none;
}
/* 导航栏可折叠（UI 强制规范）：轨道收窄为 0（DOM 保留），手柄贴底角内侧 */
.set-panel.f-nav { --set1: 0px; }
.set-nav.f-off { padding: 0; border-right-color: transparent; }
.set-tab { left: calc(var(--set1, 200px) - 17px); }
.set-panel.f-nav .set-tab { left: 0; border-radius: 0 8px 8px 0; }
.set-nav { border-right: 1px solid var(--line); background: var(--surface-2); padding: 18px 10px; overflow-y: auto; min-height: 0; }
.set-nav h2 { font-size: 16px; margin: 0 8px 14px; }
.set-nav-item {
  display: flex; align-items: center; gap: 9px; width: 100%;
  padding: 8px 10px; border-radius: var(--r-s); font-size: var(--fs-sm); color: var(--ink-2); text-align: left;
}
.set-nav-item:hover { background: var(--hover); }
.set-nav-item.on { background: var(--accent-soft); color: var(--accent); font-weight: var(--fw-medium); }
.set-nav-ic { width: 20px; text-align: center; font-size: 13px; color: var(--ink-4); flex: none; }
.set-nav-ic.txt { font-size: 11px; font-weight: 700; }
.set-nav-item.on .set-nav-ic { color: var(--accent); }
.set-stub-tag { margin-left: auto; font-size: 10px; color: var(--ink-4); border: 1px solid var(--line); border-radius: 4px; padding: 0 4px; }

.set-body { display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; }
.set-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px 12px; border-bottom: 1px solid var(--line-soft); }
.set-head h3 { font-size: 17px; margin: 0; }
.set-close { width: 30px; height: 30px; border-radius: 8px; background: var(--surface-3); font-size: 18px; color: var(--ink-3); }
.set-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 22px 30px; }

.set-sec { margin-bottom: 24px; }
.set-lb { display: block; font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--ink-3); margin-bottom: 10px; }
.set-seg { display: inline-flex; border: 1px solid var(--line); border-radius: 9px; overflow: hidden; }
.set-seg button { padding: 8px 18px; font-size: var(--fs-sm); color: var(--ink-3); }
.set-seg button.on { background: var(--accent-soft); color: var(--accent); font-weight: 650; }

.set-palette { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.set-theme { border: 1px solid var(--line); border-radius: 10px; padding: 10px; text-align: left; font-size: var(--fs-xs); }
.set-theme.on { border: 2px solid var(--accent); padding: 9px; background: var(--accent-soft); }
.set-swatch { display: block; height: 24px; border-radius: 6px; margin-bottom: 7px; }

.set-input {
  display: block; width: 100%; max-width: 380px; font: inherit; padding: 9px 12px;
  border: 1px solid var(--line); border-radius: 8px; background: var(--surface); color: var(--ink);
}
.set-row-btns { display: flex; align-items: center; gap: 14px; margin-top: 10px; }
.set-primary { padding: 8px 22px; background: var(--accent); color: #fff; border-radius: 8px; font-size: var(--fs-sm); }
.set-link { color: var(--accent); font-size: var(--fs-xs); padding: 4px 0; }
.set-note { font-size: var(--fs-xs); color: var(--ink-4); margin-top: 9px; line-height: 1.6; }

/* 套餐与邀请 */
.pc-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.pc-row .set-note { margin-top: 0; }
.pc-grow { flex: 1; min-width: 220px; }
.pc-ok { color: var(--ok, #16a34a); }
.pc-err { color: var(--danger, #dc2626); }

.set-option {
  display: flex; width: 100%; max-width: 480px; align-items: center; gap: 12px;
  border: 1px solid var(--line); border-radius: 10px; margin: 0 0 8px; padding: 11px 13px; text-align: left;
}
.set-option:hover, .set-option.on { background: var(--accent-soft); border-color: var(--accent-line); }
.set-option b { font-size: var(--fs-sm); display: block; }
.set-option small { font-size: var(--fs-xs); display: block; color: var(--ink-3); margin-top: 2px; }
.set-option span { flex: 1; min-width: 0; }
.set-option i { font-style: normal; color: var(--accent); }
select.set-input { max-width: 380px; cursor: pointer; }

.set-userhero { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.set-avatar { display: block; width: 52px; height: 52px; border-radius: 50%; object-fit: cover; background: var(--accent-soft); }
/* 无头像图账号：首字占位（与顶栏一致的文字头像口径） */
.set-avatar-txt { display: grid; place-items: center; font-size: 20px; font-weight: 650; color: var(--accent); }
/* 头像可点击更换：头像本体即按钮，hover 给品牌色描边提示 */
.set-avatar-btn { padding: 0; border: none; background: none; cursor: pointer; border-radius: 50%; flex: none; }
.set-avatar-btn:hover .set-avatar { outline: 2px solid var(--accent); outline-offset: 2px; }
.set-avatar-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.set-avatar-acts { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
.set-avatar-acts small { color: var(--ink-3, #8a94a6); }
.set-fgrid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; max-width: 560px; }
.set-field { border: 1px solid var(--line-soft); border-radius: 10px; padding: 9px 12px; background: var(--surface-2); min-width: 0; }
.set-field small { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-bottom: 3px; }
.set-field b { display: block; font-size: var(--fs-sm); font-weight: 550; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.set-field-wide { grid-column: span 3; }
.set-inline-x { display: flex; gap: 8px; align-items: center; }
.set-inline .set-select { flex: 1; min-width: 0; }
.set-inline button { flex: none; border: 1px solid var(--line); border-radius: 8px; background: var(--surface); color: var(--ink); font: inherit; font-size: var(--fs-sm); padding: 6px 14px; cursor: pointer; }
.set-inline button:hover { border-color: var(--accent); color: var(--accent); }
.set-select {
  display: block; width: 100%; font: inherit; font-size: var(--fs-sm); font-weight: 550;
  padding: 6px 8px; border: 1px solid var(--line); border-radius: 7px;
  background: var(--surface); color: var(--ink); cursor: pointer;
}
.set-select:focus { border-color: var(--accent); outline: none; box-shadow: 0 0 0 2px var(--accent-soft); }
.set-tags { display: flex; flex-wrap: wrap; gap: 5px; }
.set-tag-item {
  display: inline-block; font-size: var(--fs-2xs); padding: 3px 9px; border-radius: 6px;
  background: var(--accent-soft); color: var(--accent); border: 1px solid var(--accent-line);
}

/* 账号与安全 */
.sec-m { display: flex; align-items: center; gap: 11px; max-width: 540px; padding: 10px 0; border-bottom: 1px solid var(--line-soft); }
.sec-m:last-child { border-bottom: none; }
.sec-m-ic {
  width: 32px; height: 32px; flex: none; border-radius: 8px;
  display: grid; place-items: center;
  background: var(--surface-3); color: var(--ink-4); font-size: 10px; font-weight: 700;
}
.sec-m-ic.on { background: var(--accent-soft); color: var(--accent); }
.sec-m-b { flex: 1; min-width: 0; }
.sec-m-b b { display: flex; align-items: center; gap: 7px; font-size: var(--fs-sm); font-weight: 550; }
.sec-m-b small { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-top: 2px; }
.sec-m-tag { font-style: normal; font-size: 10px; font-weight: 550; padding: 1px 7px; border-radius: 5px; background: var(--teal-soft); color: var(--teal); flex: none; }
.sec-m-act { flex: none; font-size: var(--fs-xs); color: var(--accent); padding: 4px 8px; border-radius: 6px; }
.sec-m-act:hover { background: var(--accent-soft); }
.sec-sub { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-top: 2px; font-weight: 400; }
.sec-log { display: flex; align-items: center; gap: 12px; max-width: 540px; padding: 8px 0; font-size: var(--fs-xs); border-bottom: 1px solid var(--line-soft); }
.sec-log:last-child { border-bottom: none; }
.sec-log-t { flex: none; width: 100px; color: var(--ink-4); font-variant-numeric: tabular-nums; }
.sec-log-b { flex: 1; min-width: 0; color: var(--ink-2); }
.sec-log-ok { flex: none; color: var(--teal); font-weight: 600; }
.sec-log-ok.bad { color: var(--red); }

/* 移动端主页面配置 */
.mb-tip { margin: 0 0 10px; }
.mb-row { display: flex; align-items: center; gap: 10px; max-width: 480px; padding: 8px 0; border-bottom: 1px solid var(--line-soft); }
.mb-row:last-of-type { border-bottom: none; }
.mb-row.off .mb-idx, .mb-row.off .mb-nm { opacity: .45; }
.mb-idx { width: 20px; height: 20px; flex: none; border-radius: 6px; background: var(--surface-3); color: var(--ink-3); font-size: 11px; display: grid; place-items: center; font-variant-numeric: tabular-nums; }
.mb-nm { flex: 1; min-width: 0; font-size: var(--fs-sm); font-weight: 550; color: var(--ink-2); }
.mb-ops { display: flex; gap: 4px; flex: none; }
.mb-btn { width: 22px; height: 22px; border-radius: 6px; background: var(--surface-3); color: var(--ink-3); font-size: 12px; line-height: 1; }
.mb-btn:hover:not(:disabled) { background: var(--accent-soft); color: var(--accent); }
.mb-btn:disabled { opacity: .35; cursor: default; }

/* 助手偏好 */
.as-desc { margin: 0 0 14px; max-width: 560px; }
.as-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.as-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 999px; font-size: var(--fs-xs);
  border: 1px solid var(--line); background: var(--surface); color: var(--ink-3);
}
.as-chip i { font-style: normal; font-size: 11px; opacity: .7; }
.as-chip:hover { border-color: color-mix(in srgb, var(--accent) 40%, transparent); color: var(--accent); }
.as-chip.on {
  border-color: color-mix(in srgb, var(--accent) 34%, transparent);
  background: var(--accent-soft); color: var(--accent); font-weight: 550;
}
.as-chip.on i { opacity: 1; }
.as-chip.custom b { font-style: normal; font-weight: 400; margin-left: 2px; opacity: .6; cursor: pointer; }
.as-chip.custom b:hover { opacity: 1; }
.as-add { display: flex; gap: 8px; margin-top: 12px; }
.as-add input { flex: 1; max-width: 380px; height: 32px; padding: 0 10px; font-size: var(--fs-sm); }
.as-add button { height: 32px; padding: 0 14px; border-radius: 7px; background: var(--accent); color: #fff; font-size: var(--fs-xs); font-weight: 550; }
.as-add button:hover { filter: brightness(1.08); }
.as-live {
  display: flex; align-items: flex-start; gap: 10px;
  margin-top: 14px; padding: 12px 14px; max-width: 560px;
  background: var(--accent-soft); border: 1px solid color-mix(in srgb, var(--accent) 18%, transparent);
  border-radius: var(--r-s);
}
.as-ai {
  flex: none; width: 26px; height: 26px; border-radius: 7px;
  background: linear-gradient(140deg, var(--accent), var(--violet)); color: #fff;
  display: grid; place-items: center; font-size: 10px; font-weight: var(--fw-bold); margin-top: 1px;
}
.as-live p { flex: 1; margin: 0; font-size: var(--fs-xs); color: var(--ink-2); line-height: 1.7; }
.as-live p b { color: var(--accent); }

/* 助手形象 */
.as-id { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.as-id-av {
  flex: none; width: 44px; height: 44px; border-radius: 12px;
  background: linear-gradient(140deg, var(--accent), var(--violet)); color: #fff;
  font-size: 19px; font-weight: var(--fw-bold); display: grid; place-items: center;
}
.as-id-info b { display: block; font-size: 15px; }
.as-id-info small { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-top: 2px; }
.as-avs { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 12px; }
.as-av {
  width: 36px; height: 36px; border-radius: 9px; border: 1px solid var(--line);
  background: var(--surface); font-size: 16px; display: grid; place-items: center;
}
.as-av:hover { border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
.as-av.on { border-color: var(--accent); background: var(--accent-soft); }
.as-av-custom {
  width: 56px; height: 36px; text-align: center; font-size: 15px;
  border: 1px dashed var(--line); border-radius: 9px; background: var(--surface); color: var(--ink);
}
.as-av-custom:focus { border-color: var(--accent); outline: none; }
.as-name { max-width: 240px; }

/* 场景定制 */
.sc-add { display: flex; gap: 8px; align-items: flex-start; }
.sc-add textarea { flex: 1; max-width: 460px; min-height: 52px; padding: 8px 10px; font-size: var(--fs-sm); line-height: 1.5; resize: vertical; }
.sc-add button { height: 32px; padding: 0 14px; border-radius: 7px; background: var(--accent); color: #fff; font-size: var(--fs-xs); font-weight: 550; white-space: nowrap; }
.sc-add button:hover:not(:disabled) { filter: brightness(1.08); }
.sc-add button:disabled { opacity: .5; cursor: default; }
.sc-card {
  margin-top: 12px; max-width: 560px; border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--line));
  border-radius: 10px; padding: 12px; background: var(--surface-2);
}
.sc-row { margin-bottom: 10px; }
.sc-row > small { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-bottom: 4px; }
.sc-row input, .sc-row textarea {
  width: 100%; padding: 6px 9px; font-size: var(--fs-sm); line-height: 1.5;
  border: 1px solid var(--line); border-radius: 7px; background: var(--surface); color: var(--ink);
}
.sc-row input:focus, .sc-row textarea:focus { border-color: var(--accent); outline: none; }
.sc-row textarea { resize: vertical; }
.sc-q { display: flex; gap: 6px; align-items: center; margin-bottom: 6px; }
.sc-q input { flex: 1; }
.sc-q-x {
  width: 26px; height: 26px; border-radius: 6px; font-size: 14px; line-height: 1;
  color: var(--ink-3); border: 1px solid var(--line); background: var(--surface); flex-shrink: 0;
}
.sc-q-x:hover { color: var(--danger, #d64545); border-color: color-mix(in srgb, #d64545 45%, var(--line)); }
.sc-item {
  display: flex; align-items: center; gap: 10px; max-width: 560px;
  padding: 9px 12px; margin-top: 8px; border: 1px solid var(--line); border-radius: 9px; background: var(--surface);
}
.sc-item-b { flex: 1; min-width: 0; }
.sc-item-b b { display: block; font-size: var(--fs-sm); }
.sc-item-b small { display: block; font-size: var(--fs-2xs); color: var(--ink-4); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* RBAC */
.rbac-summary { display: flex; gap: 12px; margin-bottom: 24px; }
.rbac-stat { flex: 1; text-align: center; border: 1px solid var(--line); border-radius: 12px; padding: 14px 8px; background: var(--surface-2); }
.rbac-stat b { display: block; font-size: 22px; font-weight: 700; color: var(--accent); }
.rbac-stat small { font-size: var(--fs-2xs); color: var(--ink-4); }

.rbac-role-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }
.rbac-role-card { border: 1px solid var(--line); border-radius: 10px; padding: 12px; background: var(--surface); }
.rbac-role-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.rbac-role-head b { font-size: var(--fs-sm); }
.rbac-role-src { font-size: var(--fs-2xs); padding: 1px 6px; border-radius: 4px; background: var(--surface-3); color: var(--ink-3); }
.rbac-role-src.sys { background: var(--accent-soft); color: var(--accent); }
.rbac-role-card small { font-size: var(--fs-2xs); color: var(--ink-4); }
.rbac-role-card p { font-size: var(--fs-xs); color: var(--ink-3); margin: 5px 0 0; line-height: 1.5; }

.rbac-search { max-width: 100%; margin-bottom: 0; }
.rbac-group { border: 1px solid var(--line-soft); border-radius: 8px; margin-bottom: 6px; overflow: hidden; }
.rbac-group-head {
  display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px;
  background: var(--surface-2); font-size: var(--fs-sm); text-align: left; cursor: pointer;
}
.rbac-group-head:hover { background: var(--hover); }
.rbac-chev { font-size: 11px; color: var(--ink-4); flex: none; width: 14px; }
.rbac-group-head b { font-weight: 550; }
.rbac-group-head i { margin-left: auto; font-style: normal; font-size: var(--fs-2xs); color: var(--ink-4); }
.rbac-group-body { padding: 8px 10px; display: flex; flex-wrap: wrap; gap: 5px; }
.set-userhero b { font-size: 16px; }
.set-userhero small { display: block; font-size: var(--fs-xs); color: var(--ink-4); margin-top: 3px; }
.set-tag { display: inline-block; font-size: var(--fs-2xs); padding: 2px 7px; border-radius: 5px; background: var(--surface-3); color: var(--ink-3); margin-left: 8px; }

.set-stub-sec { border: 1px dashed var(--line); border-radius: 12px; padding: 16px; }
.set-stub-sec .set-lb { margin-bottom: 6px; }

/* 数据与隐私 · 记忆条目 */
.pv-mem {
  display: flex; align-items: flex-start; gap: 12px; max-width: 560px;
  padding: 9px 0; border-bottom: 1px solid var(--line-soft);
  font-size: var(--fs-sm); color: var(--ink-2); line-height: 1.6;
}
.pv-mem span { flex: 1; min-width: 0; }
.pv-mem .set-link { flex: none; }

.set-toggle-row { display: flex; align-items: center; justify-content: space-between; max-width: 420px; padding: 9px 0; font-size: var(--fs-sm); color: var(--ink-2); }
/* 通知方式（来源 × 重要程度 → 渠道多选下拉；级别/渠道选项数据驱动，见 data/mock/notify.ts） */
.nm-block { max-width: 460px; padding: 4px 0 10px; }
.nm-block.off { opacity: .45; }
.nm-title { font-size: var(--fs-sm); font-weight: 600; color: var(--ink-2); padding: 4px 0 6px; }
.nm-title small { font-weight: 400; color: var(--ink-3); }
.nm-row { position: relative; display: flex; align-items: center; gap: 10px; padding: 4px 0; }
.nm-lv { flex: none; width: 36px; font-size: var(--fs-xs); color: var(--ink-2); }
.nm-sel { flex: 1; min-width: 0; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 6px 10px; border: 1px solid var(--line); border-radius: 8px; background: #fff; font-size: var(--fs-xs); color: var(--ink-2); cursor: pointer; transition: .15s; }
.nm-sel:hover:not(:disabled), .nm-sel[aria-expanded="true"] { border-color: var(--accent); }
.nm-sel:disabled { cursor: not-allowed; }
.nm-sum { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nm-sum.empty { color: var(--ink-3); }
.nm-caret { flex: none; color: var(--ink-3); font-size: var(--fs-xs); }
.nm-sel[aria-expanded="true"] .nm-caret { transform: rotate(180deg); }
.nm-mask { position: fixed; inset: 0; z-index: 40; }
.nm-pop { position: absolute; top: calc(100% + 4px); left: 46px; right: 0; z-index: 50; max-height: 280px; overflow-y: auto; background: #fff; border: 1px solid var(--line); border-radius: 10px; box-shadow: 0 8px 24px #0001, 0 2px 8px #0001; padding: 4px; }
.nm-pop.up { top: auto; bottom: calc(100% + 4px); }
.nm-pop.up .nm-acts { border-top: none; border-bottom: 1px solid var(--line); margin: 0 0 4px; padding: 4px 8px 6px; order: -1; }
.nm-pop.up { display: flex; flex-direction: column; }
.nm-opt { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 6px; font-size: var(--fs-xs); color: var(--ink-2); cursor: pointer; }
.nm-opt:hover { background: var(--surface-2); }
.nm-ck { flex: none; width: 14px; height: 14px; border-radius: 4px; border: 1px solid var(--line); display: inline-flex; align-items: center; justify-content: center; color: transparent; transition: .15s; }
.nm-ck.on { border-color: var(--accent); background: var(--accent); color: #fff; }
.nm-acts { display: flex; justify-content: flex-end; gap: 12px; border-top: 1px solid var(--line); margin-top: 4px; padding: 6px 8px 4px; }
.nm-acts button { background: none; border: none; padding: 0; font-size: var(--fs-xs); color: var(--accent); cursor: pointer; }
.set-toggle { width: 38px; height: 21px; border-radius: 12px; background: var(--line); position: relative; transition: .15s; flex: none; }
.set-toggle i { position: absolute; top: 2px; left: 2px; width: 17px; height: 17px; border-radius: 50%; background: #fff; transition: .15s; box-shadow: 0 1px 3px #0003; }
.set-toggle.on { background: var(--accent); }
.set-toggle.on i { left: 19px; }

.set-kbd-row { display: flex; align-items: center; gap: 14px; padding: 8px 0; font-size: var(--fs-sm); color: var(--ink-2); border-bottom: 1px solid var(--line-soft); }
.set-kbd-row kbd {
  min-width: 64px; text-align: center; padding: 4px 10px;
  background: var(--surface-3); border: 1px solid var(--line); border-bottom-width: 2px; border-radius: 6px;
  font-family: var(--mono); font-size: var(--fs-xs); color: var(--ink-2);
}
.set-about { font-size: var(--fs-sm); color: var(--ink-2); line-height: 2; }
.set-about b { font-size: 15px; }

@media (max-width: 760px) {
  .set-panel { grid-template-columns: 1fr; height: calc(100dvh - 40px); width: calc(100vw - 24px); }
  /* 窄屏导航整体隐藏：折叠手柄一并隐藏 */
  .set-nav { display: none; }
  .set-tab { display: none; }
}
</style>
